class StepSurveyComponent extends HTMLElement {
  constructor() {
    super();
    
    this.formData = {};
  }

  connectedCallback() {
    // Get attributes
    this.step = this.getAttribute('step') || '0';
    this.fromPrevious = this.getAttribute('fromPrevious') || '';
    this.toNext = this.getAttribute('toNext') || '';
    this.collectData = this.getAttribute('collectData') === 'true';
    this.tableName = this.getAttribute('tableName') || '';
    this.maxWidth = this.getAttribute('max-width') || '756px';
    
    // Apply max-width
    this.style.maxWidth = this.maxWidth;
    
    // Initialize step
    this.init();
  }

  init() {
    // Find form if exists
    this.form = this.querySelector('form');
    
    // Find buttons
    this.previousButton = this.querySelector('button[previous]');
    this.nextButton = this.querySelector('button[next]');
    this.submitButton = this.querySelector('button[submit]');
    this.finishButton = this.querySelector('button[finish]');
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Initialize floating labels
    this.initFloatingLabels();
    
    // Initialize datepickers
    this.initDatepickers();
    
    // Initialize country selector
    this.initCountrySelector();
    
    // Initialize Medicare card conditional
    this.initMedicareCardConditional();
    
    // Initialize symptom checkboxes (Step 3)
    this.initSymptomCheckboxes();
    
    // Initialize symptoms date conditional (Step 3)
    this.initSymptomsDateConditional();
    
    // Initialize regular partner conditional (Step 2)
    this.initRegularPartnerConditional();
    
    // Initialize Step 4 conditionals
    this.initSTIDiagnosedConditional();
    this.initViralDiagnosisCheckboxes();
    
    // Initialize custom dropdowns
    this.initCustomDropdowns();
    
    // Initialize Step 6 self-collection routing
    this.initSelfCollectionRouting();
    
    // Initialize period pain conditional (Step 5)
    this.initPeriodPainConditional();
    
    // Initialize pelvic pain conditional (Step 6)
    this.initPelvicPainConditional();
    
    // Initialize treatment other conditional (Step 8)
    this.initTreatmentOtherConditional();
    
    // Initialize call timing conditional (Step 10)
    this.initCallTimingConditional();
    
    // Initialize Medicare conditional for cervical screening
    this.initCervicalMedicareConditional();
    
    // Initialize Medicare conditional for gynaecology consult
    this.initGynaecologyMedicareConditional();
    
    // Initialize phone number validation
    this.initPhoneValidation();
    
    console.log('🔧 Step initialized:', this.step);
  }

  initFloatingLabels() {
    const textFields = this.querySelectorAll('.text-field input');
    
    textFields.forEach(input => {
      // Check if input has value on load
      const checkValue = () => {
        if (input.value.trim() !== '') {
          input.classList.add('has-value');
        } else {
          input.classList.remove('has-value');
        }
      };
      
      // Check initial value
      checkValue();
      
      // Add event listeners
      input.addEventListener('input', checkValue);
      input.addEventListener('change', checkValue);
      input.addEventListener('blur', checkValue);
    });

    // Handle textarea fields
    const textAreas = this.querySelectorAll('.text-area-field textarea');
    
    textAreas.forEach(textarea => {
      // Check if textarea has value on load
      const checkValue = () => {
        if (textarea.value.trim() !== '') {
          textarea.classList.add('has-value');
        } else {
          textarea.classList.remove('has-value');
        }
      };
      
      // Check initial value
      checkValue();
      
      // Add event listeners
      textarea.addEventListener('input', checkValue);
      textarea.addEventListener('change', checkValue);
      textarea.addEventListener('blur', checkValue);
    });

    // Handle select fields
    const selectFields = this.querySelectorAll('.text-field select');
    
    selectFields.forEach(select => {
      // Check if select has value on load
      const checkValue = () => {
        if (select.value && select.value !== '') {
          select.classList.add('has-value');
        } else {
          select.classList.remove('has-value');
        }
      };
      
      // Check initial value
      checkValue();
      
      // Add event listeners
      select.addEventListener('change', checkValue);
    });
  }

  initDatepickers() {
    // Load flatpickr library if not already loaded
    if (!window.flatpickr) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/flatpickr/dist/flatpickr.min.css';
      document.head.appendChild(link);
      
      // Load monthSelect plugin CSS
      const monthSelectCss = document.createElement('link');
      monthSelectCss.rel = 'stylesheet';
      monthSelectCss.href = 'https://cdn.jsdelivr.net/npm/flatpickr/dist/plugins/monthSelect/style.css';
      document.head.appendChild(monthSelectCss);
      
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/flatpickr';
      script.onload = () => {
        // Load monthSelect plugin after flatpickr
        const monthSelectScript = document.createElement('script');
        monthSelectScript.src = 'https://cdn.jsdelivr.net/npm/flatpickr/dist/plugins/monthSelect/index.js';
        monthSelectScript.onload = () => this.setupDatepickers();
        document.head.appendChild(monthSelectScript);
      };
      document.head.appendChild(script);
    } else if (!window.monthSelectPlugin) {
      // Flatpickr loaded but monthSelect plugin not loaded
      const monthSelectCss = document.createElement('link');
      monthSelectCss.rel = 'stylesheet';
      monthSelectCss.href = 'https://cdn.jsdelivr.net/npm/flatpickr/dist/plugins/monthSelect/style.css';
      document.head.appendChild(monthSelectCss);
      
      const monthSelectScript = document.createElement('script');
      monthSelectScript.src = 'https://cdn.jsdelivr.net/npm/flatpickr/dist/plugins/monthSelect/index.js';
      monthSelectScript.onload = () => this.setupDatepickers();
      document.head.appendChild(monthSelectScript);
    } else {
      this.setupDatepickers();
    }
  }

  setupDatepickers() {
    const dateInputs = this.querySelectorAll('.date-field input');
    
    dateInputs.forEach(input => {
      const isExpiryDate = input.name && input.name.includes('expiry-date');
      
      if (isExpiryDate) {
        window.flatpickr(input, {
          dateFormat: 'm/Y',
          allowInput: false,
          plugins: [
            new window.monthSelectPlugin({
              shorthand: true,
              dateFormat: 'm/Y',
              altFormat: 'F Y'
            })
          ],
          onChange: () => {
            input.classList.add('has-value');
          }
        });
      } else {
        window.flatpickr(input, {
          dateFormat: 'm/d/Y',
          allowInput: true,
          onChange: () => {
            input.classList.add('has-value');
          }
        });
      }
    });
  }

  initCountrySelector() {
    console.log('🌍 initCountrySelector called');
    const selector = this.querySelector('.country-code-selector');
    console.log('🌍 selector found:', selector);
    if (!selector) {
      console.log('❌ No country-code-selector found');
      return;
    }

    // Load countries data
    console.log('🌍 Loading countries data...');
    this.loadCountriesData().then(countries => {
      console.log('🌍 Countries loaded:', countries.length);
      this.countries = countries;
      this.renderCountryList(countries);
      this.setupCountrySelector();
    });
  }

  initMedicareCardConditional() {
    console.log('💳 Initializing Medicare card conditional');
    const medicareRadios = this.querySelectorAll('input[name="medicare-card"]');
    const medicareCardSection = this.querySelector('.step-section:has(.medicare-card-image)');
    
    if (!medicareRadios.length || !medicareCardSection) {
      console.log('💳 Medicare elements not found in this step');
      return;
    }

    // Initially hide the Medicare card details section
    medicareCardSection.style.display = 'none';
    console.log('💳 Medicare card section initially hidden');

    // Add change listeners to radio buttons
    // Add change listeners to radio buttons
    medicareRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        console.log('💳 Medicare selection changed:', e.target.value);
        if (e.target.value === 'yes') {
          medicareCardSection.style.display = 'flex';
          console.log('💳 Medicare card section shown');
        } else {
          medicareCardSection.style.display = 'none';
          console.log('💳 Medicare card section hidden');
        }
      });
    });
  }

  initSymptomCheckboxes() {
    console.log('🩺 Initializing symptom checkboxes');
    const symptomCheckboxes = this.querySelectorAll('input[name="symptoms"]');
    
    if (!symptomCheckboxes.length) {
      console.log('🩺 No symptom checkboxes found in this step');
      return;
    }

    const noSymptomsCheckbox = this.querySelector('input[name="symptoms"][value="no-symptoms"]');
    const otherCheckboxes = Array.from(symptomCheckboxes).filter(cb => cb.value !== 'no-symptoms');

    if (!noSymptomsCheckbox) {
      console.log('🩺 No "no-symptoms" checkbox found');
      return;
    }

    // When "No symptoms" is checked, uncheck all others
    noSymptomsCheckbox.addEventListener('change', (e) => {
      if (e.target.checked) {
        console.log('🩺 "No symptoms" checked - unchecking all others');
        otherCheckboxes.forEach(cb => {
          cb.checked = false;
        });
      }
    });

    // When any other symptom is checked, uncheck "No symptoms"
    otherCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        if (e.target.checked && noSymptomsCheckbox.checked) {
          console.log('🩺 Other symptom checked - unchecking "No symptoms"');
          noSymptomsCheckbox.checked = false;
        }
      });
    });
  }

  initSymptomsDateConditional() {
    console.log('📅 Initializing symptoms date conditional');
    const symptomCheckboxes = this.querySelectorAll('input[name="symptoms"]');
    const symptomsDateContainer = this.querySelector('#symptoms-date-container');
    
    if (!symptomCheckboxes.length || !symptomsDateContainer) {
      console.log('📅 Symptoms date elements not found in this step');
      return;
    }

    const noSymptomsCheckbox = this.querySelector('input[name="symptoms"][value="no-symptoms"]');
    
    const updateDateVisibility = () => {
      const checkedSymptoms = this.querySelectorAll('input[name="symptoms"]:checked');
      const hasNoSymptoms = noSymptomsCheckbox && noSymptomsCheckbox.checked;
      const hasOtherSymptoms = Array.from(checkedSymptoms).some(cb => cb.value !== 'no-symptoms');
      
      if (hasNoSymptoms || checkedSymptoms.length === 0) {
        symptomsDateContainer.style.display = 'none';
        console.log('📅 Symptoms date hidden (no symptoms or none selected)');
      } else if (hasOtherSymptoms) {
        symptomsDateContainer.style.display = 'block';
        console.log('📅 Symptoms date shown (has symptoms)');
      }
    };

    // Initially hide
    symptomsDateContainer.style.display = 'none';

    // Add change listeners
    symptomCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', updateDateVisibility);
    });
  }

  initRegularPartnerConditional() {
    console.log('👫 Initializing regular partner conditional');
    const partnerRadios = this.querySelectorAll('input[name="regular-partner"]');
    const partnerSymptomsContainer = this.querySelector('#partner-symptoms-container');
    
    if (!partnerRadios.length || !partnerSymptomsContainer) {
      console.log('👫 Regular partner elements not found in this step');
      return;
    }

    // Initially hide
    partnerSymptomsContainer.style.display = 'none';
    console.log('👫 Partner symptoms container initially hidden');

    // Add change listeners
    partnerRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        console.log('👫 Regular partner selection changed:', e.target.value);
        if (e.target.value === 'yes') {
          partnerSymptomsContainer.style.display = 'block';
          console.log('👫 Partner symptoms container shown');
        } else {
          partnerSymptomsContainer.style.display = 'none';
          console.log('👫 Partner symptoms container hidden');
        }
      });
    });
  }

  initSTIDiagnosedConditional() {
    console.log('🦠 Initializing STI diagnosed conditional');
    const stiRadios = this.querySelectorAll('input[name="sti-diagnosed"]');
    const stiDetailsContainer = this.querySelector('#sti-details-container');
    
    if (!stiRadios.length || !stiDetailsContainer) {
      console.log('🦠 STI diagnosed elements not found in this step');
      return;
    }

    // Initially hide the STI details section
    stiDetailsContainer.style.display = 'none';
    console.log('🦠 STI details section initially hidden');

    // Add change listeners to radio buttons
    stiRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        console.log('🦠 STI diagnosed selection changed:', e.target.value);
        if (e.target.value === 'yes') {
          stiDetailsContainer.style.display = 'flex';
          console.log('🦠 STI details section shown');
        } else {
          stiDetailsContainer.style.display = 'none';
          console.log('🦠 STI details section hidden');
        }
      });
    });
  }

  initViralDiagnosisCheckboxes() {
    console.log('🦠 Initializing viral diagnosis checkboxes');
    const viralCheckboxes = this.querySelectorAll('input[name="viral-diagnosis"]');
    
    if (!viralCheckboxes.length) {
      console.log('🦠 No viral diagnosis checkboxes found in this step');
      return;
    }

    const noCheckbox = this.querySelector('input[name="viral-diagnosis"][value="no"]');
    const otherCheckboxes = Array.from(viralCheckboxes).filter(cb => cb.value !== 'no');

    if (!noCheckbox) {
      console.log('🦠 No "no" checkbox found');
      return;
    }

    // When "No" is checked, uncheck all others
    noCheckbox.addEventListener('change', (e) => {
      if (e.target.checked) {
        console.log('🦠 "No" checked - unchecking all others');
        otherCheckboxes.forEach(cb => {
          cb.checked = false;
        });
      }
    });

    // When any other checkbox is checked, uncheck "No"
    otherCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        if (e.target.checked && noCheckbox.checked) {
          console.log('🦠 Other checkbox checked - unchecking "No"');
          noCheckbox.checked = false;
        }
      });
    });
  }

  initSelfCollectionRouting() {
    console.log('🧪 Initializing self-collection routing');
    
    // This method doesn't need to do anything during initialization
    // The routing logic is handled in the nextButton click handler
    // Just keeping this method for consistency with other initializers
    
    if (this.step !== '6') {
      console.log('🧪 Not step 6, skipping self-collection routing setup');
      return;
    }
    
    console.log('🧪 Step 6 detected - routing logic will be handled in next button click');
  }

  initCustomDropdowns() {
    console.log('📋 Initializing custom dropdowns');
    const dropdowns = this.querySelectorAll('.custom-dropdown');
    
    if (!dropdowns.length) {
      console.log('📋 No custom dropdowns found in this step');
      return;
    }

    dropdowns.forEach(dropdown => {
      const trigger = dropdown.querySelector('.custom-dropdown-trigger');
      const menu = dropdown.querySelector('.custom-dropdown-menu');
      const text = dropdown.querySelector('.custom-dropdown-text');
      const checkboxes = dropdown.querySelectorAll('.custom-dropdown-option input[type="checkbox"]');
      const dropdownId = dropdown.getAttribute('data-dropdown-id');
      const selectedItemsP = dropdownId ? dropdown.closest('.custom-dropdown-wrapper')?.querySelector(`[data-dropdown-id="${dropdownId}"].custom-dropdown-selected-items`) : null;
      
      if (!trigger || !menu || !text) {
        console.log('📋 Missing dropdown elements');
        return;
      }

      // Toggle dropdown on trigger click
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const isActive = dropdown.classList.toggle('active');
        console.log('📋 Dropdown toggled:', isActive);
        
        // Close other dropdowns
        this.querySelectorAll('.custom-dropdown').forEach(other => {
          if (other !== dropdown) {
            other.classList.remove('active');
          }
        });
      });

      // Update trigger text and selected items list when checkboxes change
      const updateTriggerText = () => {
        const checkedBoxes = Array.from(checkboxes).filter(cb => cb.checked);
        const count = checkedBoxes.length;
        
        if (count === 0) {
          text.textContent = 'Select STI diagnosed';
          if (selectedItemsP) {
            selectedItemsP.style.display = 'none';
          }
        } else if (count === 1) {
          const label = checkedBoxes[0].parentElement.querySelector('.custom-dropdown-label');
          text.textContent = label.textContent;
          if (selectedItemsP) {
            selectedItemsP.style.display = 'none';
          }
        } else {
          text.textContent = `${count} selected`;
          if (selectedItemsP) {
            const labels = checkedBoxes.map(cb => 
              cb.parentElement.querySelector('.custom-dropdown-label').textContent
            );
            selectedItemsP.textContent = labels.join(', ');
            selectedItemsP.style.display = 'block';
          }
        }
        
        console.log('📋 Dropdown text updated:', text.textContent);
      };

      // Add change listeners to all checkboxes
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', updateTriggerText);
      });

      // Prevent menu from closing when clicking options
      menu.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.custom-dropdown')) {
        this.querySelectorAll('.custom-dropdown').forEach(dropdown => {
          dropdown.classList.remove('active');
        });
      }
    });
  }

  async loadCountriesData() {
    try {
      console.log('🌍 Fetching countries from GitHub...');
      const response = await fetch('https://raw.githubusercontent.com/dr5hn/countries-states-cities-database/master/countries.json');
      console.log('🌍 Fetch response status:', response.status);
      const data = await response.json();
      console.log('🌍 Raw data length:', data.length);
      
      // Transform to needed format
      const transformed = data
        .filter(country => country.phone_code)
        .map(country => ({
          name: country.name,
          code: country.iso2,
          dialCode: country.phone_code,
          flag: country.emoji || this.getCountryFlag(country.iso2)
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      console.log('🌍 Transformed countries:', transformed.length);
      return transformed;
    } catch (error) {
      console.error('❌ Failed to load countries:', error);
      // Fallback to basic list
      console.log('🌍 Using fallback country list');
      return [
        { name: 'United States', code: 'US', dialCode: '+1', flag: '🇺🇸' },
        { name: 'United Kingdom', code: 'GB', dialCode: '+44', flag: '🇬🇧' },
        { name: 'Canada', code: 'CA', dialCode: '+1', flag: '🇨🇦' },
        { name: 'Australia', code: 'AU', dialCode: '+61', flag: '🇦🇺' },
        { name: 'India', code: 'IN', dialCode: '+91', flag: '🇮🇳' },
      ];
    }
  }

  getCountryFlag(countryCode) {
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  }

  renderCountryList(countries) {
    console.log('🌍 renderCountryList called with', countries.length, 'countries');
    const countryList = this.querySelector('.country-list');
    console.log('🌍 countryList element:', countryList);
    if (!countryList) {
      console.log('❌ No .country-list element found');
      return;
    }

    countryList.innerHTML = countries.map(country => `
      <div class="country-item" data-code="${country.code}" data-dial-code="${country.dialCode}">
        <span class="country-flag">${country.flag}</span>
        <div class="country-info">
          <span class="country-name">${country.name}</span>
          <span class="country-dial-code">${country.dialCode}</span>
        </div>
      </div>
    `).join('');
    console.log('🌍 Country list HTML rendered');
  }

  setupCountrySelector() {
    console.log('🌍 setupCountrySelector called');
    const selector = this.querySelector('.country-code-selector');
    const dropdown = this.querySelector('.country-dropdown');
    const searchInput = this.querySelector('.country-search');
    const countryList = this.querySelector('.country-list');
    const selectedCode = this.querySelector('.selected-code');

    console.log('🌍 Elements found:', {
      selector: !!selector,
      dropdown: !!dropdown,
      searchInput: !!searchInput,
      countryList: !!countryList,
      selectedCode: !!selectedCode
    });

    if (!selector || !dropdown || !searchInput || !countryList) {
      console.log('❌ Missing required elements for country selector');
      return;
    }

    // Toggle dropdown
    console.log('🌍 Adding click listener to selector');
    selector.addEventListener('click', (e) => {
      console.log('🌍 Selector clicked!');
      e.stopPropagation();
      const isActive = dropdown.classList.toggle('active');
      selector.classList.toggle('active', isActive);
      console.log('🌍 Dropdown active:', isActive);
      
      if (isActive) {
        searchInput.value = '';
        // Don't re-render if we already have items, just re-attach listeners
        if (this.querySelector('.country-item')) {
          console.log('🌍 Country items already exist, just re-attaching listeners');
          this.attachCountryItemListeners();
        } else {
          console.log('🌍 No country items, rendering list');
          this.renderCountryList(this.countries);
          this.attachCountryItemListeners();
        }
        setTimeout(() => searchInput.focus(), 100);
      }
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!selector.contains(e.target) && !dropdown.contains(e.target)) {
        dropdown.classList.remove('active');
        selector.classList.remove('active');
      }
    });

    // Search functionality
    searchInput.addEventListener('input', (e) => {
      const searchTerm = e.target.value.toLowerCase();
      const filtered = this.countries.filter(country => 
        country.name.toLowerCase().includes(searchTerm) ||
        country.dialCode.includes(searchTerm)
      );
      this.renderCountryList(filtered);
      this.attachCountryItemListeners();
    });

    // Attach item listeners
    this.attachCountryItemListeners();
  }

  attachCountryItemListeners() {
    console.log('🌍 attachCountryItemListeners called');
    const countryItems = this.querySelectorAll('.country-item');
    const selectedCode = this.querySelector('.selected-code');
    const dropdown = this.querySelector('.country-dropdown');
    const selector = this.querySelector('.country-code-selector');

    console.log('🌍 Found', countryItems.length, 'country items');
    console.log('🌍 selectedCode element:', selectedCode);
    console.log('🌍 selectedCode current textContent:', selectedCode?.textContent);
    console.log('🌍 selectedCode outerHTML:', selectedCode?.outerHTML);
    console.log('🌍 dropdown element:', dropdown);
    console.log('🌍 selector element:', selector);

    countryItems.forEach((item, index) => {
      console.log(`🌍 Adding listener to item ${index}:`, item.dataset.dialCode, item.dataset.code);
      item.addEventListener('click', (e) => {
        console.log('🌍🌍🌍 === COUNTRY ITEM CLICKED ===');
        console.log('🌍 Event target:', e.target);
        console.log('🌍 Item clicked:', item);
        console.log('🌍 Dial code from dataset:', item.dataset.dialCode);
        console.log('🌍 Country code from dataset:', item.dataset.code);
        
        e.stopPropagation();
        const dialCode = item.dataset.dialCode;
        console.log('🌍 Extracted dialCode variable:', dialCode);
        
        // Update selected code display
        console.log('🌍 About to update selectedCode...');
        console.log('🌍 selectedCode element exists?', !!selectedCode);
        console.log('🌍 selectedCode before update:', selectedCode?.textContent);
        
        if (selectedCode) {
          const oldValue = selectedCode.textContent;
          selectedCode.textContent = dialCode;
          const newValue = selectedCode.textContent;
          console.log('🌍 OLD value:', oldValue);
          console.log('🌍 NEW value:', newValue);
          console.log('🌍 Values are different?', oldValue !== newValue);
          console.log('🌍 selectedCode innerHTML:', selectedCode.innerHTML);
          console.log('🌍 selectedCode outerHTML:', selectedCode.outerHTML);
        } else {
          console.log('❌❌❌ selectedCode element NOT FOUND!');
        }
        
        // Update selected state
        console.log('🌍 Updating selected state...');
        countryItems.forEach(i => i.classList.remove('selected'));
        item.classList.add('selected');
        console.log('🌍 Item now has selected class:', item.classList.contains('selected'));
        
        // Close dropdown
        console.log('🌍 Closing dropdown...');
        dropdown.classList.remove('active');
        selector.classList.remove('active');
        console.log('🌍 Dropdown is active?', dropdown.classList.contains('active'));
        console.log('🌍 Selector is active?', selector.classList.contains('active'));
        
        // Trigger has-value on phone input
        const phoneInput = this.querySelector('#phone-number');
        if (phoneInput && phoneInput.value.trim()) {
          phoneInput.classList.add('has-value');
        }
        
        console.log('🌍🌍🌍 === END COUNTRY ITEM CLICK ===');
      });
    });
    
    console.log('🌍 Finished attaching listeners to all items');
  }

  initPhoneValidation() {
    console.log('📞 Initializing phone validation');
    const phoneInputs = this.querySelectorAll('input[type="tel"]');
    
    if (!phoneInputs.length) {
      console.log('📞 No phone inputs found in this step');
      return;
    }

    phoneInputs.forEach(input => {
      // Only allow numbers, spaces, dashes, and parentheses
      input.addEventListener('input', (e) => {
        const originalValue = e.target.value;
        const cleanedValue = originalValue.replace(/[^0-9\s\-()]/g, '');
        if (originalValue !== cleanedValue) {
          e.target.value = cleanedValue;
          console.log('📞 Cleaned phone input:', cleanedValue);
        }
      });

      // Prevent non-numeric keys
      input.addEventListener('keypress', (e) => {
        const allowedChars = /[0-9\s\-()]/;
        if (!allowedChars.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
          e.preventDefault();
          console.log('📞 Blocked non-numeric key:', e.key);
        }
      });
    });
  }

  validateStep() {
    console.log('✅ Validating step:', this.step);
    let isValid = true;
    const errors = [];

    // Helper function to get human-readable field name from input
    const getFieldLabel = (input) => {
      // Try to find associated label
      const label = this.querySelector(`label[for="${input.id}"]`);
      if (label) {
        return label.textContent.trim();
      }
      
      // Fallback: humanize the name/id
      const fieldName = input.name || input.id || 'This field';
      return fieldName
        .replace(/-cervical$/, '')
        .replace(/-gynaecology$/, '')
        .replace(/-/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    };

    // Helper function to humanize radio/checkbox group names
    const humanizeGroupName = (name) => {
      return name
        .replace(/-cervical$/, '')
        .replace(/-gynaecology$/, '')
        .replace(/-/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    };

    // Helper function to check if element is visible (not in a hidden container)
    const isElementVisible = (element) => {
      let parent = element.parentElement;
      while (parent && parent !== this) {
        const style = window.getComputedStyle(parent);
        if (style.display === 'none' || style.visibility === 'hidden') {
          return false;
        }
        parent = parent.parentElement;
      }
      return true;
    };

    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Get all required inputs in this step
    const requiredInputs = this.querySelectorAll('input[required], select[required], textarea[required]');
    
    requiredInputs.forEach(input => {
      // Skip validation for hidden fields (e.g., Medicare fields when "No" is selected)
      if (!isElementVisible(input)) {
        console.log('⏭️ Skipping hidden required field:', input.name || input.id);
        input.classList.remove('error');
        return;
      }
      
      if (!input.value.trim()) {
        isValid = false;
        errors.push(`${getFieldLabel(input)} is required`);
        input.classList.add('error');
      } else if (input.type === 'email' || input.id === 'email-address' || input.name === 'email-address') {
        // Validate email format
        if (!emailRegex.test(input.value.trim())) {
          isValid = false;
          errors.push('Please enter a valid email address');
          input.classList.add('error');
        } else {
          input.classList.remove('error');
        }
      } else {
        input.classList.remove('error');
      }
    });

    // Check for radio groups - at least one must be selected
    const radioGroups = this.querySelectorAll('.radio-group');
    radioGroups.forEach(group => {
      // Skip hidden radio groups
      if (!isElementVisible(group)) {
        console.log('⏭️ Skipping hidden radio group');
        group.classList.remove('error');
        return;
      }
      
      const radios = group.querySelectorAll('input[type="radio"]');
      if (radios.length > 0) {
        const groupName = radios[0].name;
        const isChecked = this.querySelector(`input[name="${groupName}"]:checked`);
        if (!isChecked) {
          isValid = false;
          errors.push(`Please select an option for ${humanizeGroupName(groupName)}`);
          group.classList.add('error');
        } else {
          group.classList.remove('error');
        }
      }
    });

    // Check for checkbox groups that require at least one selection
    const checkboxGroups = this.querySelectorAll('.checkbox-group-wrapper');
    checkboxGroups.forEach(group => {
      // Skip hidden checkbox groups
      if (!isElementVisible(group)) {
        console.log('⏭️ Skipping hidden checkbox group');
        group.classList.remove('error');
        return;
      }
      
      const checkboxes = group.querySelectorAll('input[type="checkbox"]');
      if (checkboxes.length > 0) {
        const isChecked = Array.from(checkboxes).some(cb => cb.checked);
        if (!isChecked) {
          isValid = false;
          const groupName = checkboxes[0].name || 'checkbox group';
          errors.push(`Please select at least one option for ${humanizeGroupName(groupName)}`);
          group.classList.add('error');
        } else {
          group.classList.remove('error');
        }
      }
    });

    if (!isValid) {
      console.log('❌ Validation failed:', errors);
      // Show first error
      this.showValidationError(errors[0]);
    } else {
      console.log('✅ Validation passed');
      this.hideValidationError();
    }

    return isValid;
  }

  showValidationError(message) {
    // Remove existing error message
    this.hideValidationError();
    
    // Create error message element
    const errorDiv = document.createElement('div');
    errorDiv.className = 'validation-error-message';
    errorDiv.textContent = message;
    errorDiv.style.cssText = 'color: #dc3545; font-size: 14px; margin-top: 10px; padding: 10px; background: #fff5f5; border: 1px solid #dc3545; border-radius: 4px;';
    
    // Insert before button group
    const buttonGroup = this.querySelector('.button-group');
    if (buttonGroup) {
      buttonGroup.parentNode.insertBefore(errorDiv, buttonGroup);
    }
  }

  hideValidationError() {
    const existingError = this.querySelector('.validation-error-message');
    if (existingError) {
      existingError.remove();
    }
  }

  setupEventListeners() {
    // Previous button
    if (this.previousButton) {
      this.previousButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.dispatchEvent(new CustomEvent('survey:previous', {
          bubbles: true,
          detail: { step: this.step }
        }));
      });
    }
    
    // Next button
    if (this.nextButton) {
      this.nextButton.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Validate step before proceeding
        if (!this.validateStep()) {
          console.log('🚫 Validation failed, not proceeding');
          return;
        }
        
        // Check for conditional navigation based on Medicare selection
        let nextStep = this.toNext;
        
        // If this is step 1 and user selected "No" for Medicare, go to step 1A
        if (this.step === '1') {
          // Check for STI survey Medicare field
          const medicareRadio = this.querySelector('input[name="medicare-card"]:checked');
          if (medicareRadio && medicareRadio.value === 'no') {
            nextStep = '1A';
            console.log('💳 Medicare "No" selected, routing to step 1A');
          }
          
          // Check for Cervical screening survey Medicare field
          const medicareCervicalRadio = this.querySelector('input[name="medicare-card-cervical"]:checked');
          if (medicareCervicalRadio && medicareCervicalRadio.value === 'no') {
            nextStep = '1A';
            console.log('💳 Cervical Medicare "No" selected, routing to step 1A');
          }
        }
        
        // If this is step 1A, check private billing consent and route accordingly
        if (this.step === '1A') {
          const consentRadio = this.querySelector('input[name="private-billing-consent"]:checked');
          if (consentRadio) {
            if (consentRadio.value === 'no') {
              nextStep = '1B';
              console.log('🚫 Private billing "No" selected, routing to step 1B (exit)');
            } else if (consentRadio.value === 'yes') {
              nextStep = '2';
              console.log('✅ Private billing "Yes" selected, routing to step 2 (Referral)');
            }
          }
        }
        
        // If this is step 2, check GP referral and route accordingly
        if (this.step === '2') {
          const referralRadio = this.querySelector('input[name="gp-referral"]:checked');
          if (referralRadio) {
            if (referralRadio.value === 'no') {
              nextStep = '2A';
              console.log('🚫 GP referral "No" selected, routing to step 2A (exit - referral required)');
            } else if (referralRadio.value === 'yes') {
              nextStep = '3';
              console.log('✅ GP referral "Yes" selected, routing to step 3 (Upload file)');
            }
          }
        }
        
        // If this is step 3 (Symptom Screening), check for trigger symptoms
        if (this.step === '3') {
          const checkedSymptoms = this.querySelectorAll('input[name="symptoms"]:checked');
          console.log('🩺 Step 3 routing - checked symptoms:', checkedSymptoms.length);
          
          // Log all checked symptoms for debugging
          checkedSymptoms.forEach(checkbox => {
            console.log('  - Symptom:', checkbox.value, '| data-trigger:', checkbox.getAttribute('data-trigger'));
          });
          
          const hasTriggerSymptom = Array.from(checkedSymptoms).some(checkbox => 
            checkbox.getAttribute('data-trigger') === 'true'
          );
          
          console.log('🩺 Has trigger symptom?', hasTriggerSymptom);
          
          if (hasTriggerSymptom) {
            nextStep = '3A';
            console.log('⚠️ Trigger symptom detected, routing to step 3A (GP referral exit)');
          } else {
            nextStep = '4';
            console.log('✅ No trigger symptoms, routing to step 4 (Prior History)');
          }
        }
        
        // If this is step 6 (Self-Collection), check eligibility
        if (this.step === '6') {
          // Check for STI survey fields
          let confidentSwab = this.querySelector('input[name="confident-swab"]:checked');
          let physicalLimitations = this.querySelector('input[name="physical-limitations"]:checked');
          
          // Check for Cervical screening survey fields
          const confidentSwabCervical = this.querySelector('input[name="confident-swab-cervical"]:checked');
          const physicalLimitationsCervical = this.querySelector('input[name="physical-limitations-cervical"]:checked');
          
          // Use cervical fields if STI fields not found
          if (!confidentSwab && confidentSwabCervical) {
            confidentSwab = confidentSwabCervical;
          }
          if (!physicalLimitations && physicalLimitationsCervical) {
            physicalLimitations = physicalLimitationsCervical;
          }
          
          // ONLY Q1=YES AND Q2=NO → go to Step 7 (submit)
          // All other combinations → go to Step 6A (exit)
          if (confidentSwab && physicalLimitations) {
            if (confidentSwab.value === 'yes' && physicalLimitations.value === 'no') {
              nextStep = '7';
              console.log('✅ Self-collection eligible: confident=yes, limitations=no, routing to step 7 (Submit)');
            } else {
              nextStep = '6A';
              console.log('🚫 Self-collection not eligible, routing to step 6A (GP referral exit)');
            }
          }
        }
        
        // CERVICAL SCREENING ELIGIBILITY LOGIC
        
        // Step 2: Check age eligibility (< 25 years old = not eligible)
        if (this.step === '2') {
          const ageInput = this.querySelector('input[name="age-cervical"]');
          if (ageInput && ageInput.value) {
            const age = parseInt(ageInput.value);
            if (age < 25) {
              nextStep = '6A';
              console.log('🚫 Cervical: Age < 25, routing to step 6A (GP required)');
            }
          }
        }
        
        // Step 3: Check for any symptoms (any symptom except "none" = not eligible)
        if (this.step === '3') {
          const cervicalSymptoms = this.querySelectorAll('input[name="symptoms-cervical"]:checked');
          const hasActualSymptoms = Array.from(cervicalSymptoms).some(cb => cb.value !== 'none');
          if (hasActualSymptoms) {
            nextStep = '6A';
            console.log('🚫 Cervical: Symptoms detected, routing to step 6A (GP required)');
          }
        }
        
        // Step 4: Check high-risk factors
        if (this.step === '4') {
          const diagnosedHSIL = this.querySelector('input[name="diagnosed-hsil-cervical"]:checked');
          const immunocompromised = this.querySelector('input[name="immunocompromised-cervical"]:checked');
          
          // If diagnosed with HSIL = Yes OR immunocompromised = Yes → not eligible
          if (diagnosedHSIL && diagnosedHSIL.value === 'yes') {
            nextStep = '6A';
            console.log('🚫 Cervical: HSIL/AIS/cancer diagnosed, routing to step 6A (GP required)');
          } else if (immunocompromised && immunocompromised.value === 'yes') {
            nextStep = '6A';
            console.log('🚫 Cervical: Immunocompromised, routing to step 6A (GP required)');
          }
        }
        
        // Step 5: Check additional triage questions (any Yes = not eligible)
        if (this.step === '5') {
          const abnormalSymptoms = this.querySelector('input[name="abnormal-symptoms-cervical"]:checked');
          const glandularAbnormality = this.querySelector('input[name="glandular-abnormality-cervical"]:checked');
          const surveillance = this.querySelector('input[name="surveillance-cervical"]:checked');
          const hysterectomyHSIL = this.querySelector('input[name="hysterectomy-hsil-cervical"]:checked');
          const desExposure = this.querySelector('input[name="des-exposure-cervical"]:checked');
          
          if ((abnormalSymptoms && abnormalSymptoms.value === 'yes') ||
              (glandularAbnormality && glandularAbnormality.value === 'yes') ||
              (surveillance && surveillance.value === 'yes') ||
              (hysterectomyHSIL && hysterectomyHSIL.value === 'yes') ||
              (desExposure && desExposure.value === 'yes')) {
            nextStep = '6A';
            console.log('🚫 Cervical: Additional triage risk factor detected, routing to step 6A (GP required)');
          }
        }
        
        this.dispatchEvent(new CustomEvent('survey:next', {
          bubbles: true,
          detail: { 
            step: this.step,
            toNext: nextStep
          }
        }));
      });
    }
    
    // Submit button
    if (this.submitButton) {
      this.submitButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleFormSubmit();
      });
    }
    
    // Finish button
    if (this.finishButton) {
      this.finishButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleFinish();
      });
    }
    
    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleFormSubmit();
      });
    }
  }

  handleFormSubmit() {
    if (this.form && this.collectData) {
      // Collect form data
      const formData = new FormData(this.form);
      const data = {};
      
      for (let [key, value] of formData.entries()) {
        data[key] = value;
      }
      
      this.formData = data;
      
      // Dispatch data collection event
      this.dispatchEvent(new CustomEvent('survey:collectData', {
        bubbles: true,
        detail: {
          tableName: this.tableName,
          formData: data
        }
      }));
    }
    
    // Dispatch submit event
    this.dispatchEvent(new CustomEvent('survey:submit', {
      bubbles: true,
      detail: { 
        step: this.step,
        toNext: this.toNext
      }
    }));
  }

  handleFinish() {
    // Collect data if needed
    if (this.form && this.collectData) {
      const formData = new FormData(this.form);
      const data = {};
      
      for (let [key, value] of formData.entries()) {
        data[key] = value;
      }
      
      this.formData = data;
      
      // Dispatch data collection event
      this.dispatchEvent(new CustomEvent('survey:collectData', {
        bubbles: true,
        detail: {
          tableName: this.tableName,
          formData: data
        }
      }));
    }
    
    // Dispatch finish event
    this.dispatchEvent(new CustomEvent('survey:finish', {
      bubbles: true,
      detail: { step: this.step }
    }));
  }

  initPeriodPainConditional() {
    console.log('🩺 Initializing period pain conditional');
    const periodPainRadios = this.querySelectorAll('input[name="period-pain"]');
    const severityContainer = this.querySelector('#period-pain-severity');
    const durationContainer = this.querySelector('#period-pain-duration');
    
    if (!periodPainRadios.length || !severityContainer || !durationContainer) {
      console.log('🩺 Period pain elements not found in this step');
      return;
    }

    // Add change listeners to period pain radio buttons
    periodPainRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        console.log('🩺 Period pain selection changed:', e.target.value);
        if (e.target.value === 'yes') {
          severityContainer.style.display = 'flex';
          durationContainer.style.display = 'flex';
          console.log('🩺 Showing severity and duration questions');
        } else {
          severityContainer.style.display = 'none';
          durationContainer.style.display = 'none';
          console.log('🩺 Hiding severity and duration questions');
        }
      });
    });
  }

  initPelvicPainConditional() {
    console.log('🩺 Initializing pelvic pain conditional');
    const pelvicPainRadios = this.querySelectorAll('input[name="pelvic-pain-outside"]');
    const whenContainer = this.querySelector('#pelvic-pain-when');
    
    if (!pelvicPainRadios.length || !whenContainer) {
      console.log('🩺 Pelvic pain elements not found in this step');
      return;
    }

    // Add change listeners to pelvic pain radio buttons
    pelvicPainRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        console.log('🩺 Pelvic pain outside period selection changed:', e.target.value);
        if (e.target.value === 'yes') {
          whenContainer.style.display = 'flex';
          console.log('🩺 Showing "when do you notice it" question');
        } else {
          whenContainer.style.display = 'none';
          console.log('🩺 Hiding "when do you notice it" question');
        }
      });
    });
  }

  initTreatmentOtherConditional() {
    console.log('💊 Initializing treatment other conditional');
    const treatmentCheckboxes = this.querySelectorAll('input[name="treatments"]');
    const otherSpecifyContainer = this.querySelector('#treatment-other-specify');
    
    if (!treatmentCheckboxes.length || !otherSpecifyContainer) {
      console.log('💊 Treatment elements not found in this step');
      return;
    }

    // Find the "Other" checkbox
    const otherCheckbox = Array.from(treatmentCheckboxes).find(cb => cb.value === 'other');
    
    if (!otherCheckbox) {
      console.log('💊 Other checkbox not found');
      return;
    }

    // Add change listener to "Other" checkbox
    otherCheckbox.addEventListener('change', (e) => {
      console.log('💊 Other checkbox changed:', e.target.checked);
      if (e.target.checked) {
        otherSpecifyContainer.style.display = 'flex';
        console.log('💊 Showing "Please specify" field');
      } else {
        otherSpecifyContainer.style.display = 'none';
        console.log('💊 Hiding "Please specify" field');
      }
    });
  }

  initCallTimingConditional() {
    console.log('📞 Initializing call timing conditional');
    
    const callTimingRadios = this.querySelectorAll('input[name="call-timing-ok"]');
    const alternateContactContainer = this.querySelector('#alternate-contact-container');
    
    if (!callTimingRadios.length || !alternateContactContainer) {
      console.log('📞 Call timing elements not found in this step');
      return;
    }

    // Add change listeners to radio buttons
    callTimingRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        console.log('📞 Call timing changed:', e.target.value);
        if (e.target.value === 'no') {
          alternateContactContainer.style.display = 'block';
          console.log('📞 Showing alternate contact field');
        } else {
          alternateContactContainer.style.display = 'none';
          console.log('📞 Hiding alternate contact field');
        }
      });
    });
  }

  initCervicalMedicareConditional() {
    console.log('💳 Initializing cervical Medicare conditional');
    
    const medicareRadios = this.querySelectorAll('input[name="medicare-card-cervical"]');
    const medicareCardDetails = this.querySelector('#medicare-card-details-cervical');
    
    if (!medicareRadios.length || !medicareCardDetails) {
      console.log('💳 Cervical Medicare elements not found in this step');
      return;
    }

    // Add change listeners to radio buttons
    medicareRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        console.log('💳 Medicare card cervical changed:', e.target.value);
        if (e.target.value === 'yes') {
          medicareCardDetails.style.display = 'flex';
          console.log('💳 Showing Medicare card details');
        } else {
          medicareCardDetails.style.display = 'none';
          console.log('💳 Hiding Medicare card details');
        }
      });
    });
  }

  initGynaecologyMedicareConditional() {
    console.log('💳 Initializing gynaecology Medicare conditional');
    
    // Check if we're in the gynaecology survey by looking for the specific Medicare card section
    const medicareRadios = this.querySelectorAll('input[name="medicare-card"]');
    const medicareSection = this.querySelector('.step-section:has(.medicare-card-image)');
    
    if (!medicareRadios.length || !medicareSection) {
      console.log('💳 Gynaecology Medicare elements not found in this step');
      return;
    }

    // Initially hide the Medicare card details section
    medicareSection.style.display = 'none';
    console.log('💳 Medicare card details initially hidden');

    // Add change listeners to radio buttons
    medicareRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        console.log('💳 Gynaecology Medicare card changed:', e.target.value);
        if (e.target.value === 'yes') {
          medicareSection.style.display = 'block';
          console.log('💳 Showing Medicare card details');
        } else {
          medicareSection.style.display = 'none';
          console.log('💳 Hiding Medicare card details');
        }
      });
    });
  }

  // Populate exit reasons for cervical screening Step 6A
  populateCervicalExitReasons() {
    const reasonsContainer = document.querySelector('#cervical-exit-reasons');
    if (!reasonsContainer) return;

    const reasons = [];
    const surveyComponent = document.querySelector('#cervical-screening-survey');
    if (!surveyComponent) return;

    // Check age
    const ageInput = surveyComponent.querySelector('input[name="age-cervical"]');
    if (ageInput && parseInt(ageInput.value) < 25) {
      reasons.push('You are under 25 years old');
    }

    // Check symptoms
    const symptoms = surveyComponent.querySelectorAll('input[name="symptoms-cervical"]:checked');
    if (symptoms.length > 0) {
      const symptomLabels = Array.from(symptoms).map(cb => {
        const label = cb.closest('.checkbox-option')?.querySelector('.checkbox-label');
        return label ? label.textContent.trim() : cb.value;
      });
      reasons.push(`Are you currently experiencing any of the following? ${symptomLabels.join(', ')}`);
    }

    // Check HSIL diagnosis
    const hsil = surveyComponent.querySelector('input[name="diagnosed-hsil-cervical"]:checked');
    if (hsil && hsil.value === 'yes') {
      reasons.push('Have you ever been diagnosed with HSIL, AIS, or cervical cancer? Yes');
    }

    // Check immunocompromised
    const immunocompromised = surveyComponent.querySelector('input[name="immunocompromised-cervical"]:checked');
    if (immunocompromised && immunocompromised.value === 'yes') {
      reasons.push('Are you immunocompromised (e.g., HIV, transplant, long-term steroids)? Yes');
    }

    // Check abnormal symptoms
    const abnormal = surveyComponent.querySelector('input[name="abnormal-symptoms-cervical"]:checked');
    if (abnormal && abnormal.value === 'yes') {
      reasons.push('Do you have abnormal bleeding, discharge or pain? Yes');
    }

    // Check glandular abnormality
    const glandular = surveyComponent.querySelector('input[name="glandular-abnormality-cervical"]:checked');
    if (glandular && glandular.value === 'yes') {
      reasons.push('Have you ever been diagnosed with a cervical glandular abnormality or malignancy? Yes');
    }

    // Check surveillance
    const surveillance = surveyComponent.querySelector('input[name="surveillance-cervical"]:checked');
    if (surveillance && surveillance.value === 'yes') {
      reasons.push('Are you undergoing surveillance or "Test of Cure"? Yes');
    }

    // Check hysterectomy with HSIL
    const hysterectomy = surveyComponent.querySelector('input[name="hysterectomy-hsil-cervical"]:checked');
    if (hysterectomy && hysterectomy.value === 'yes') {
      reasons.push('Have you had a total hysterectomy with past HSIL? Yes');
    }

    // Check DES exposure
    const des = surveyComponent.querySelector('input[name="des-exposure-cervical"]:checked');
    if (des && des.value === 'yes') {
      reasons.push('Were you exposed to DES (diethylstilbestrol) in utero? Yes');
    }

    // Populate the reasons container
    if (reasons.length > 0) {
      reasonsContainer.innerHTML = reasons.map(reason => `
        <div class="exit-reason-item" style="background: #f5f5f5; padding: 12px 16px; margin-bottom: 8px; border-radius: 4px;">
          <p style="margin: 0; color: #666; font-size: 14px;">${reason}</p>
        </div>
      `).join('');
    }
  }
}


customElements.define('step-survey-component', StepSurveyComponent);
